import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ImageIcon, SearchInput } from '@saleshandy/design-system';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Help } from '@saleshandy/icons';
import Modal from '../../../ui/modal';
import Checkbox from '../../atoms/checkbox';
import DNDField from './components/dnd-field';
import { RootState } from '../../../../../store/root-reducer';
import { Images } from '../../../../app-constants';
import { OverlayTooltip, Placement } from '../../overlay';
import {
  getContactListByViewIdRequest,
  getProspectListCountRequest,
  getProspectListRequest,
} from '../../../../../components/prospect/extra-actions';
import { ViewType } from '../../../../../components/prospect/enums/prospect';
import { setHasAppliedKanbanSelectedFields } from '../../../../../components/prospect/prospect-slice';
import { EditCardColumnsModalProps, Field } from './types';
import { defaultKanbanPageSize } from '../../../../../components/prospect/components/prospect-kanban/utils/helper';
import {
  getDisplayField,
  getFilterIdFromLabel,
  getMockProspectData,
  getPreviewFieldValue,
} from './helper';
import toaster from '../../../../toaster';

/**
 * EditCardColumnsModal
 *
 * @param show - Boolean to check if the modal is shown
 * @param onCancel - Function to cancel the modal
 * @param onSave - Function to save the modal
 * @param selectedFields - Array of selected fields
 * @param columnGroups - Array of column groups
 * @param priorityArray - Array of priority array
 * @param onResetColumns - Function to reset the columns
 * @returns React component for configuring Kanban card fields
 */
const EditCardColumnsModal: React.FC<EditCardColumnsModalProps> = ({
  show,
  onCancel,
  onSave,
  selectedFields,
  columnGroups,
  priorityArray,
  setProspectViewIdFilter,
}) => {
  // **  Hooks
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state?.prospect?.filters);
  const fields = useSelector((state: RootState) => state?.prospect?.fields);
  const activeViewId = useSelector(
    (state: RootState) => state?.prospect?.activeViewId || null,
  );
  const selectedKanbanCategoryId = useSelector(
    (state: RootState) => state?.prospect?.selectedKanbanCategoryId,
  );

  // ** States
  const [search, setSearch] = useState('');
  const [columns, setColumns] = useState(columnGroups);
  const [selectedColumns, setSelectedColumns] = useState<Field[]>(
    selectedFields?.filter((field) => field?.label !== undefined),
  );
  const [newlyAddedColumns, setNewlyAddedColumns] = useState<Field[]>([]);

  // ** Helpers
  /**
   * Check if two fields match (by ID or label)
   */
  const doFieldsMatch = useCallback((field1: Field, field2: Field): boolean => {
    // Exact ID match
    if (
      field1?.label?.toString()?.toLowerCase() ===
      field2?.id?.toString()?.toLowerCase()
    ) {
      return true;
    }
    if (field1?.id === field2?.id) return true;
    // If IDs are not same, match by LABEL
    return (
      field1?.label?.trim()?.toLowerCase() ===
      field2?.label?.trim()?.toLowerCase()
    );
  }, []);

  const getIsFieldSelected = useCallback(
    (field: Field): boolean =>
      selectedColumns?.some((selectedField) =>
        doFieldsMatch(selectedField, field),
      ),
    [selectedColumns, doFieldsMatch],
  );

  /**
   * Toggle a field in the selected columns list.
   * - Removes it when already selected
   * - Adds it when not selected (up to a maximum of 5 fields)
   */
  const handleOnToggle = (field: Field) => {
    const isFieldSelected = getIsFieldSelected(field);

    if (isFieldSelected) {
      setSelectedColumns((prevColumns) =>
        prevColumns.filter((existing) => !doFieldsMatch(existing, field)),
      );
      setNewlyAddedColumns((prevNewlyAddedColumns) =>
        prevNewlyAddedColumns?.filter(
          (existing) => !doFieldsMatch(existing, field),
        ),
      );
      return;
    }

    // Do not allow more than 5 selected fields
    if (selectedColumns.length >= 5) {
      return;
    }

    setSelectedColumns((prevColumns) => {
      const nextColumns = [...(prevColumns || []), field];
      if (!priorityArray?.length) {
        return nextColumns;
      }
      return [...nextColumns].sort((a, b) => {
        const indexA = priorityArray.indexOf(a.id.toString());
        const indexB = priorityArray.indexOf(b.id.toString());
        return (
          (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
          (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
        );
      });
    });

    const isNewField = !selectedFields.find((f) => f.id === field.id);
    if (isNewField) {
      setNewlyAddedColumns((prevNewlyAddedColumns) => [
        ...(prevNewlyAddedColumns || []),
        field,
      ]);
    }
  };

  /**
   * Apply selected fields and refresh the Kanban list for the current view.
   */
  const handleOnSave = useCallback(() => {
    dispatch(setHasAppliedKanbanSelectedFields(true));
    onSave(selectedColumns, newlyAddedColumns);
    setProspectViewIdFilter?.(null);

    if (activeViewId && selectedKanbanCategoryId) {
      dispatch(
        getContactListByViewIdRequest({
          pageNum: 1,
          pageSize: defaultKanbanPageSize,
          viewType: ViewType.Kanban,
          selectedField: selectedColumns?.map((field) =>
            getFilterIdFromLabel(field?.id.toString(), fields),
          ),
          kanbanViewId: selectedKanbanCategoryId,
          viewId: activeViewId,
        }),
      );
      toaster.success('Columns updated successfully');
    } else if (selectedKanbanCategoryId) {
      dispatch(
        getProspectListRequest({
          pageNum: 1,
          pageSize: defaultKanbanPageSize,
          viewType: ViewType.Kanban,
          selectedField: selectedColumns?.map((field) =>
            getFilterIdFromLabel(field?.id?.toString(), fields),
          ),
          kanbanViewId: selectedKanbanCategoryId,
          filterGroupLogic: filters?.filterGroupLogic,
          search: filters?.search,
        }),
      );
      toaster.success('Columns updated successfully');
    }
    dispatch(
      getProspectListCountRequest({
        viewType: ViewType.Kanban,
        search: filters?.search,
        viewId: activeViewId,
      }),
    );

    dispatch(
      getProspectListCountRequest({
        viewType: ViewType.List,
        viewId: activeViewId,
      }),
    );
  }, [
    activeViewId,
    selectedKanbanCategoryId,
    selectedColumns,
    newlyAddedColumns,
    dispatch,
    onSave,
    fields,
    filters,
    search,
  ]);

  const getFieldIndex = (fieldId: Field['id']): number =>
    selectedColumns?.findIndex((field) => field.id === fieldId);

  /**
   * Handle drag-and-drop reordering of selected fields.
   */
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!active || !over || active.id === over.id) return;

      setSelectedColumns((cols) => {
        const from = getFieldIndex(active.id);
        const to = getFieldIndex(over.id);

        const newCols = arrayMove(cols, from, to);

        return newCols;
      });
    },
    [selectedColumns, getFieldIndex, setSelectedColumns, arrayMove],
  );

  /**
   * Filter available fields based on the search text.
   */
  const onSearchChange = useCallback(
    (searchText: string) => {
      setSearch(searchText);

      const lowercasedSearchText = searchText.toLowerCase();

      setColumns(
        columnGroups
          .map((group) => ({
            ...group,
            fields: group.fields.filter((field) =>
              field.label.toLowerCase().includes(lowercasedSearchText),
            ),
          }))
          .filter((group) => group.fields.length > 0),
      );
    },
    [columnGroups],
  );

  const getFieldClassNames = useCallback(
    (field: Field) =>
      classNames([
        'field',
        {
          checked: getIsFieldSelected(field),
        },
        {
          disabled: selectedColumns?.length >= 5 && !getIsFieldSelected(field),
        },
      ]),
    [getIsFieldSelected],
  );

  /**
   * Mock prospect data for card preview
   * This represents sample data that matches the field labels
   */
  const mockProspectData = useMemo(() => getMockProspectData(), []);

  /**
   * Get field value for preview based on field label and id
   */
  const getPreviewFieldValueCallback = useCallback(
    (field: Field): string | React.ReactNode | null =>
      getPreviewFieldValue(field, mockProspectData, fields, columnGroups),
    [mockProspectData, fields, columnGroups],
  );

  /**
   * Render preview fields based on selected columns
   */
  const renderPreviewFields = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return null;
    }

    return selectedColumns.map((field, index) => {
      const isFirstField = index === 0;
      const fieldValue = getPreviewFieldValueCallback(field);

      if (fieldValue === null || fieldValue === '') {
        return null;
      }

      if (isFirstField) {
        return (
          <span key={field.id} className="title">
            {typeof fieldValue === 'string' ? fieldValue : fieldValue}
          </span>
        );
      }

      return (
        <React.Fragment key={field.id}>
          {typeof fieldValue === 'string' ? (
            <span>{fieldValue}</span>
          ) : (
            fieldValue
          )}
        </React.Fragment>
      );
    });
  }, [selectedColumns, getPreviewFieldValueCallback]);

  // ** Effects
  useEffect(() => {
    setColumns(columnGroups);
  }, [columnGroups]);

  return (
    <Modal
      show={show}
      title="Edit Card"
      className="edit-card-column"
      variant="border"
      showCloseButton={false}
      onHide={onCancel}
      primaryBtnClickedHandler={handleOnSave}
      primaryBtnDisabled={selectedColumns?.length === 0}
      secondaryBtnClickedHandler={onCancel}
      primaryBtnLabel="Apply"
    >
      <div className="edit-card-container">
        <div className="col-left">
          <h3 className="col-title">Available Fields</h3>
          <SearchInput
            search={search}
            setSearch={onSearchChange}
            onSearch={() => null}
            onClearSearch={() => {
              onSearchChange('');
            }}
          />

          <div className="fields-section-container">
            {columns?.map((group) => (
              <div className="fields-section" key={group.id}>
                <h4 className="fields-title">{group.title}</h4>
                <div className="fields-container">
                  {group.fields.map((field) => (
                    <OverlayTooltip
                      key={field.id}
                      placement={Placement.BottomStart}
                      text={
                        selectedColumns.length >= 5 &&
                        !getIsFieldSelected(field)
                          ? 'Maximum 5 fields allowed. Deselect one to add another.'
                          : undefined
                      }
                      className={
                        selectedColumns.length >= 5 &&
                        !getIsFieldSelected(field)
                          ? 'selected-fields-tooltip'
                          : 'd-none'
                      }
                    >
                      <div className={getFieldClassNames(field)}>
                        <label
                          htmlFor={field.id.toString()}
                          className="field-label"
                        >
                          {selectedColumns?.length >= 5 &&
                          !getIsFieldSelected(field) ? (
                            <div
                              style={{
                                cursor:
                                  selectedColumns.length >= 5 &&
                                  !getIsFieldSelected(field)
                                    ? 'not-allowed'
                                    : 'pointer',
                              }}
                            >
                              <Checkbox
                                id={field.id.toString()}
                                checked={
                                  field?.isNonRemovable ||
                                  getIsFieldSelected(field)
                                }
                                onChange={() => handleOnToggle(field)}
                                disabled={
                                  selectedColumns.length >= 5 &&
                                  !getIsFieldSelected(field)
                                }
                              />
                            </div>
                          ) : (
                            <Checkbox
                              id={field.id.toString()}
                              checked={
                                field?.isNonRemovable ||
                                getIsFieldSelected(field)
                              }
                              onChange={() => handleOnToggle(field)}
                            />
                          )}
                          <span className="field-label-name">
                            {field?.label}
                            {/* {truncate(field?.label || '', 25, false)} */}
                          </span>
                          {fields?.find(
                            (f) => f?.id?.toString() === field?.id?.toString(),
                          )?.isAiGenerated && (
                            <ImageIcon
                              src={Images.NoResultGradient}
                              alt="ai-icon"
                              width={16}
                              height={16}
                            />
                          )}
                        </label>
                      </div>
                    </OverlayTooltip>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-right">
          <div className="col-right-header">
            <div>
              <span className="col-title">Shown in Cards</span>
              <span className="only-5"> (Only 5)</span>
            </div>
            <OverlayTooltip
              placement={Placement.Bottom}
              text="You can add up to 5 fields. The top one becomes the card title."
            >
              <Help
                width={16}
                height={16}
                color="#6B7280"
                className="cursor-pointer"
              />
            </OverlayTooltip>
          </div>

          <div className="dnd-fields-container">
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={onDragEnd}
            >
              <div className="dnd-fields">
                <SortableContext
                  items={selectedColumns}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedColumns?.map((field) => (
                    <DNDField
                      key={field?.id}
                      field={getDisplayField(field)}
                      handleOnToggle={handleOnToggle}
                      fields={fields}
                    />
                  ))}
                </SortableContext>
              </div>
            </DndContext>
          </div>
        </div>

        {renderPreviewFields && (
          <div className="card-preview">
            <div className="d-flex flex-column gap-8">
              <span className="card-preview-title">Card Preview</span>

              <div className="d-flex flex-column gap-8 card-preview-fields">
                {renderPreviewFields}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default memo(EditCardColumnsModal);
